import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { ID } from '../../../../../types';

import {
  LifestyleDetectionID,
  LifestyleDetectionParams
} from '../../../../lifestyleDetections/lifestyleDetectionsTypes';
import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields
} from '../../../../products/productsTypes';
import { ImageSelectionPayload } from '../ThreeDStockContentImageLens';
import { ThreeDStockContentSearchInImageView } from './ThreeDStockContentSearchInImageTypes';

import { FetchLifestyleDetection } from '../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';

import { useReactQueryState } from '../../../../common/reactQuery/hooks/useReactQueryState';

import { ThreeDStockContentSearchInImageDetections } from './components/ThreeDStockContentSearchInImageDetections';

import { LifestyleDetectionCache } from '../../../../lifestyleDetections/LifestyleDetectionCache';

interface ThreeDStockContentSearchInImageProps {
  changeProductsFilters: ChangeProductsFiltersFunc;
  view: ThreeDStockContentSearchInImageView;
  onDetectionsChange?: (detections: FetchLifestyleDetection[]) => void;
  activeDetectionId?: ID | null;
  onActiveDetectionChange?: (id: ID | null) => void;
}

function ThreeDStockContentSearchInImage({
  changeProductsFilters,
  view,
  onDetectionsChange,
  activeDetectionId,
  onActiveDetectionChange
}: ThreeDStockContentSearchInImageProps) {
  const { value: params, clearValue } =
    useReactQueryState<LifestyleDetectionParams>(
      LifestyleDetectionCache.searchImageUrlCacheKey()
    );

  const handleClearSearch = useCallback<() => void>(() => {
    changeProductsFilters({}, [
      FetchProductsFilterFields.SIMILAR_PRODUCTS_BY_CUSTOM_AREA,
      FetchProductsFilterFields.ID,
      FetchProductsFilterFields.SEARCH_BY_DETECTED_ID
    ]);
    clearValue();
  }, [changeProductsFilters, clearValue]);

  const handleSelectImage = useCallback<
    (
      payload: ImageSelectionPayload,
      detectRequestId: LifestyleDetectionID
    ) => void
  >(
    (
      { selection, largestSearchResult, lifestyleDetectionId },
      detectRequestId
    ) => {
      if (lifestyleDetectionId) {
        return changeProductsFilters(
          {
            searchByDetectedId: {
              detectionId: lifestyleDetectionId,
              lifestyleId: params?.lifestyleId || null
            }
          },
          [
            FetchProductsFilterFields.SIMILAR_IMAGE,
            FetchProductsFilterFields.SIMILAR_PRODUCT,
            FetchProductsFilterFields.ID,
            FetchProductsFilterFields.SIMILAR_PRODUCTS_BY_CUSTOM_AREA
          ]
        );
      }

      if (!isEmpty(largestSearchResult)) {
        return changeProductsFilters(
          { id: { in: map(largestSearchResult, 'externalId') } },
          [
            FetchProductsFilterFields.SIMILAR_IMAGE,
            FetchProductsFilterFields.SIMILAR_PRODUCT,
            FetchProductsFilterFields.SIMILAR_PRODUCTS_BY_CUSTOM_AREA,
            FetchProductsFilterFields.SEARCH_BY_DETECTED_ID
          ]
        );
      }

      return changeProductsFilters(
        { similarProductsByCustomArea: { detectRequestId, ...selection } },
        [
          FetchProductsFilterFields.SIMILAR_IMAGE,
          FetchProductsFilterFields.SIMILAR_PRODUCT,
          FetchProductsFilterFields.SEARCH_BY_DETECTED_ID,
          FetchProductsFilterFields.ID
        ]
      );
    },
    [changeProductsFilters, params?.lifestyleId]
  );

  if (!params?.imageUrl) {
    return null;
  }

  return (
    <ThreeDStockContentSearchInImageDetections
      key={params.imageUrl}
      view={view}
      imageUrl={params.imageUrl}
      imageId={params.imageId}
      lifestyleId={params.lifestyleId}
      onSelectedImage={handleSelectImage}
      onClearSearch={handleClearSearch}
      onDetectionsChange={onDetectionsChange}
      activeDetectionId={activeDetectionId}
      onActiveDetectionChange={onActiveDetectionChange}
    />
  );
}

export default ThreeDStockContentSearchInImage;
